import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMatchStatementLine } from "@/api/queries";
import type { Id, StatementLineToReview } from "@/api/types";

/** What a form opened for a line of the bank's statement starts from. The amount is never negative. */
export interface StatementLineStart {
  statementLine: Id;
  account: Id;
  date: string;
  amount: string;
}

export function statementLineSearch(line: StatementLineToReview, account: Id): StatementLineStart {
  return {
    statementLine: line.id,
    account,
    date: line.date,
    amount: line.amount.replace("-", ""),
  };
}

/**
 * A receipt or payment form, when it was opened for a statement line. Saving matches the two and goes
 * back to the reconciliation. The entry is recorded by then, so a match that fails is said there, on
 * the screen that shows the line and the entry side by side.
 */
export function useStatementLineStart() {
  const search = useSearch({ strict: false }) as Partial<StatementLineStart>;
  const navigate = useNavigate();
  const match = useMatchStatementLine();

  const start: StatementLineStart | null =
    search.statementLine && search.account && search.date && search.amount
      ? {
          statementLine: search.statementLine,
          account: search.account,
          date: search.date,
          amount: search.amount,
        }
      : null;

  const back = (problem?: string) =>
    void navigate({
      to: "/banking/reconciliation",
      search: { account: start?.account, ...(problem ? { problem } : {}) },
    });

  const finish = async (journalId: Id | undefined) => {
    if (!start || !journalId) {
      back("Find the entry on the line's Find list and match it there.");
      return;
    }
    try {
      await match.mutateAsync({ lineId: start.statementLine, journalId });
      back();
    } catch (failure) {
      back((failure as Error).message);
    }
  };

  return { start, back, finish };
}
